import React, { memo, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Input } from '@Sage/hoi-poi-ui';

import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';

// Filter that cleans decimal input and replaces commas with dots
// Exactly like the old TextInput component
const decimalFilter = (text) => {
    let textArr = [];
    let textValue = text?.replace?.(/[^-,.0-9\s]/g, '') || '';
    textValue.split('').forEach((current, index) => {
        if (index === 0 && current !== '.' && current !== ',') {
            textArr.push(current);
        } else if (
            index > 0 &&
            (current === '.' || current === ',') &&
            !textArr.includes('.') &&
            !textArr.includes(',') &&
            !isNaN(parseInt(textArr[index - 1], 10))
        ) {
            textArr.push(current);
        } else if (current !== '-' && current !== '.' && current !== ',') {
            textArr.push(current);
        }
    });
    textValue = textArr.join('');
    textValue = textValue.replace(',', '.');
    return textValue;
};

const getPlaceholderByDecimals = (decimals, fallbackDecimals) => {
    const parsedDecimals = Number.parseInt(decimals, 10);
    const hasValidDecimals = Number.isInteger(parsedDecimals) && parsedDecimals >= 0;
    const finalDecimals = hasValidDecimals ? parsedDecimals : fallbackDecimals;

    if (finalDecimals <= 0) return '0';
    return `0.${'0'.repeat(finalDecimals)}`;
};

const getNormalizedDecimalParts = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const rawValue = value.toString().replace(/[^-,.0-9\s]/g, '');
    const normalizedValue = decimalFilter(rawValue);
    if (!normalizedValue || isNaN(normalizedValue)) return null;

    const separatorMatch = rawValue.match(/[.,]/);
    const decimalSeparator = separatorMatch?.[0] || '.';
    const hasDecimalSeparator = rawValue.includes('.') || rawValue.includes(',');
    const [integerPart, decimalPart = ''] = normalizedValue.split('.');

    return {
        rawValue,
        normalizedValue,
        decimalSeparator,
        hasDecimalSeparator,
        integerPart,
        decimalPart,
    };
};

const insertAtSelection = (currentValue, insertedValue, selectionStart, selectionEnd) => {
    return currentValue.slice(0, selectionStart) + insertedValue + currentValue.slice(selectionEnd);
};

const formatValueByDecimals = (value, limitDecimals) => {
    const normalizedParts = getNormalizedDecimalParts(value);
    if (!normalizedParts) return '';

    const { normalizedValue, hasDecimalSeparator, integerPart, decimalPart } = normalizedParts;

    if (limitDecimals === undefined || limitDecimals === null) {
        return normalizedValue;
    }

    if (!hasDecimalSeparator) {
        if (limitDecimals > 0) {
            return `${integerPart}.${'0'.repeat(limitDecimals)}`;
        }

        return integerPart;
    }

    return `${integerPart}.${decimalPart.padEnd(limitDecimals, '0')}`;
};

const clampValueByDecimals = (value, limitDecimals) => {
    const normalizedParts = getNormalizedDecimalParts(value);
    if (!normalizedParts) return '';

    const { rawValue, decimalSeparator, hasDecimalSeparator, integerPart, decimalPart } =
        normalizedParts;

    if (limitDecimals === undefined || limitDecimals === null) {
        // Use normalized string (comma → dot, valid structure), not raw stripped input.
        return normalizedParts.normalizedValue;
    }

    if (limitDecimals <= 0) {
        return integerPart;
    }

    if (!hasDecimalSeparator) {
        return integerPart;
    }

    const clampedDecimal = decimalPart.slice(0, limitDecimals);
    return clampedDecimal
        ? `${integerPart}${decimalSeparator}${clampedDecimal}`
        : `${integerPart}${decimalSeparator}`;
};

const NAVIGATION_KEYS = new Set([
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'Tab',
    'Enter',
    'Escape',
]);

const propTypes = {
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    description: PropTypes.string,
    error: PropTypes.any,
    fieldId: PropTypes.string,
    forceFocus: PropTypes.bool,
    hidden: PropTypes.bool,
    hint: PropTypes.string,
    label: PropTypes.any,
    labelMode: PropTypes.string,
    limitDecimals: PropTypes.number,
    mandatory: PropTypes.bool,
    maxLength: PropTypes.number,
    onChange: PropTypes.func,
    placeholderReadOnly: PropTypes.string,
    preComponent: PropTypes.node,
    postComponent: PropTypes.node,
    readOnly: PropTypes.bool,
    shouldRenderField: PropTypes.func,
    /** When false, blur only sanitizes to a valid int/decimal string and commits it — no padding / fixed decimals. */
    formatOnBlur: PropTypes.bool,
    /**
     * When true, ignores `limitDecimals` for clamp/format: no trailing zero padding and no fixed decimal places;
     * blur only normalizes a valid int/decimal string (same as formatOnBlur=false).
     */
    variableDecimalDigits: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const Decimal = ({
    autoFocus,
    className,
    description,
    error,
    fieldId,
    forceFocus,
    hidden,
    hint,
    label,
    labelMode,
    limitDecimals,
    mandatory,
    maxLength,
    onChange,
    placeholderReadOnly,
    preComponent,
    postComponent,
    readOnly,
    shouldRenderField,
    formatOnBlur = true,
    variableDecimalDigits = false,
    value = '',
    isFullWidth = true,
}) => {
    const effectiveLimitDecimals = variableDecimalDigits ? undefined : limitDecimals;
    const effectiveFormatOnBlur = variableDecimalDigits ? false : formatOnBlur;

    // Convert value to string, same as old component
    const initialValue = formatValueByDecimals(value, effectiveLimitDecimals);
    const [text, setText] = useState(initialValue);
    const [changedAfterSave, setChangedAfterSave] = useState(false);
    const prevValue = useRef(value);
    const prevLimitDecimals = useRef(effectiveLimitDecimals);

    // Sync with external value changes (like from server)
    // Same logic as old componentDidUpdate
    useEffect(() => {
        const hasValueChanged = prevValue.current !== value;
        const haveDecimalsChanged = prevLimitDecimals.current !== effectiveLimitDecimals;

        if (hasValueChanged || haveDecimalsChanged) {
            // Compare numeric values to avoid race condition with formatted decimals
            // e.g., 7.8700 (formatted) vs 7.87 (from state) are the same value
            const currentNum = parseFloat(text);
            const newNum = parseFloat(value);
            const bothAreNumbers = !isNaN(currentNum) && !isNaN(newNum);
            const valueStr = formatValueByDecimals(value, effectiveLimitDecimals);

            // Only update if values are actually different (numerically)
            if (bothAreNumbers) {
                if (currentNum !== newNum || (haveDecimalsChanged && valueStr !== text)) {
                    setText(valueStr);
                    setChangedAfterSave(false);
                }
            } else if (valueStr !== text) {
                setText(valueStr);
                setChangedAfterSave(false);
            }
        }
        prevValue.current = value;
        prevLimitDecimals.current = effectiveLimitDecimals;
    }, [effectiveLimitDecimals, text, value]);

    const rootClassName = useMemo(() => classnames('fm-field-decimal', className), [className]);

    // Check error length - same as old checkErrorLength
    const checkErrorLength = useCallback(
        (val) => {
            if (maxLength && maxLength > 0 && val && val.length > maxLength) {
                return getLiteralWithParameters('error_text_too_long', [maxLength]);
            }
            return '';
        },
        [maxLength],
    );

    const errorLength = useMemo(() => {
        // Apply filter before checking length
        const cleanValue = text ? decimalFilter(text) : '';
        return checkErrorLength(cleanValue);
    }, [text, checkErrorLength]);

    const placeholderText = useMemo(() => {
        if (hint) return hint;
        if (variableDecimalDigits) return '0';
        return getPlaceholderByDecimals(limitDecimals, 1);
    }, [hint, limitDecimals, variableDecimalDigits]);

    // onChange - store raw value, apply filter only for length check
    // Same as old onChange
    const onInnerChange = useCallback(
        (newValue, _, info) => {
            if (info?.action === 'clear') {
                setText('');
                setChangedAfterSave(false);
                onChange && onChange('');
                return;
            }

            const nextValue = clampValueByDecimals(newValue, effectiveLimitDecimals);
            setText(nextValue);
            setChangedAfterSave(true);
            // formatOnBlur=false: parent must stay in sync on every keystroke (blur may not run before submit).
            if (!effectiveFormatOnBlur && onChange) {
                onChange(nextValue);
            }
        },
        [onChange, effectiveLimitDecimals, effectiveFormatOnBlur],
    );

    const onKeyDown = useCallback(
        (event) => {
            // Always validate keys (when limitDecimals was skipped, letters could reach the input before clamp).
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            if (NAVIGATION_KEYS.has(event.key)) return;
            if (!/^[0-9.,-]$/.test(event.key)) {
                event.preventDefault();
                return;
            }

            const currentValue = text || '';
            const target = event.target;
            const selectionStart = target?.selectionStart ?? currentValue.length;
            const selectionEnd = target?.selectionEnd ?? currentValue.length;
            const nextValue = insertAtSelection(
                currentValue,
                event.key,
                selectionStart,
                selectionEnd,
            );
            const clampedValue = clampValueByDecimals(nextValue, effectiveLimitDecimals);

            if (nextValue !== clampedValue) {
                event.preventDefault();
            }
        },
        [effectiveLimitDecimals, text],
    );

    const onPaste = useCallback(
        (event) => {
            const pastedText = event.clipboardData?.getData('text') || '';
            if (!pastedText) return;

            const currentValue = text || '';
            const target = event.target;
            const selectionStart = target?.selectionStart ?? currentValue.length;
            const selectionEnd = target?.selectionEnd ?? currentValue.length;
            const nextValue = insertAtSelection(
                currentValue,
                pastedText,
                selectionStart,
                selectionEnd,
            );
            const clampedValue = clampValueByDecimals(nextValue, effectiveLimitDecimals);

            if (nextValue !== clampedValue) {
                event.preventDefault();
                setText(clampedValue);
                setChangedAfterSave(true);
                if (!effectiveFormatOnBlur && onChange) {
                    onChange(clampedValue);
                }
            }
        },
        [effectiveLimitDecimals, text, effectiveFormatOnBlur, onChange],
    );

    // onBlur — hoi-poi Input passes (valueFromDom, event). Always prefer DOM value: React `text` can lag one tick behind the native input.
    const onBlur = useCallback(
        (valueFromDom) => {
            const raw =
                typeof valueFromDom === 'string' ? valueFromDom : text != null ? String(text) : '';

            if (!effectiveFormatOnBlur) {
                const parts = getNormalizedDecimalParts(raw);
                const sanitized =
                    parts && Number.isFinite(Number(parts.normalizedValue))
                        ? parts.normalizedValue
                        : '';
                setText(sanitized);
                onChange && onChange(sanitized);
                return;
            }

            const finalValue = formatValueByDecimals(raw, effectiveLimitDecimals);

            if (finalValue) {
                setText(finalValue);
            }

            onChange && onChange(finalValue);
        },
        [onChange, text, effectiveLimitDecimals, effectiveFormatOnBlur],
    );

    if (hidden) return null;
    if (shouldRenderField && !shouldRenderField(fieldId)) return null;

    // Error handling - same as old component
    let finalError = error || errorLength;
    if (
        finalError &&
        finalError === getLiteralWithParameters('error_text_too_long', [maxLength]) &&
        changedAfterSave
    ) {
        finalError = errorLength;
    }

    return (
        <Input
            autoFocus={autoFocus || forceFocus}
            className={rootClassName}
            error={finalError}
            hint={description}
            isFullWidth={isFullWidth}
            isReadOnly={readOnly}
            isRequired={mandatory}
            label={label}
            labelMode={labelMode}
            onBlur={onBlur}
            onChange={onInnerChange}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            placeholder={placeholderText || getLiteral('placeholder_amount')}
            placeholderReadOnly={
                placeholderReadOnly || placeholderText || getLiteral('placeholder_amount')
            }
            preComponent={preComponent}
            postComponent={postComponent}
            type="text"
            value={text}
        />
    );
};

Decimal.displayName = 'Decimal';
Decimal.propTypes = propTypes;

export default memo(Decimal);
